import { getCaptureAssets } from './api';
import { findQueuedCapture } from './offline';
import type { QueuedCapture } from './offline';

/**
 * Where the pixels of a delivered capture come from.
 *
 * CA-05 asks the operator to confirm values against an item, and until now
 * it showed no photograph at all, so the operator was agreeing to three
 * field values with nothing on screen saying which capture they belonged
 * to. A capture now carries up to three frames, which makes the same
 * question sharper: the model number was read off one of them.
 *
 * The device is asked first. The queue record that delivered the capture
 * still holds the bytes, so the frames render with no request, no presign
 * and no connection, which is the state an operator in a plant room is
 * usually in. `GET /capture/{id}/assets` is the fallback for every other
 * case: another device, a cleared queue, a capture opened from the work
 * list days later.
 */

export interface CaptureFrameImage {
  /** The stored key, so a field's evidence can name the frame it was read off. */
  assetKey: string;
  url: string;
  /** 'device' urls are object URLs made here and revoked by the caller. */
  source: 'device' | 'server';
}

export interface FrameSourceDeps {
  findQueued: (captureId: string) => Promise<QueuedCapture | undefined>;
  fetchAssets: typeof getCaptureAssets;
  createObjectUrl: (blob: Blob) => string;
}

/**
 * The frames of `captureId`, in capture order.
 *
 * `assetKeys` comes off the capture record. The queue record holds
 * fingerprints rather than keys, and `processQueue` uploads its assets in
 * order, so the nth local frame is the nth key.
 *
 * That pairing is the whole reason the local bytes can be trusted, so the
 * device is used only while the two agree on how many frames the capture
 * has. A record holding a different number is a record that no longer
 * describes what the server stored, and showing the operator one set of
 * pixels while a reviewer sees another is worse than the request the
 * fallback costs.
 */
export async function resolveCaptureFrames(
  captureId: string,
  assetKeys: string[],
  deps: Partial<FrameSourceDeps> = {}
): Promise<CaptureFrameImage[]> {
  const findQueued = deps.findQueued ?? findQueuedCapture;
  const fetchAssets = deps.fetchAssets ?? getCaptureAssets;
  const createObjectUrl = deps.createObjectUrl ?? ((blob: Blob) => URL.createObjectURL(blob));

  const queued = await findQueued(captureId);
  if (queued && queued.assets.length > 0 && queued.assets.length === assetKeys.length) {
    return queued.assets.map((asset, index) => ({
      assetKey: assetKeys[index],
      url: createObjectUrl(new Blob([asset.bytes], { type: asset.contentType })),
      source: 'device',
    }));
  }

  // A voice or manual capture stores no asset, so there is nothing to ask
  // the server for and an empty list is the honest answer.
  if (assetKeys.length === 0) return [];

  const response = await fetchAssets(captureId);
  return response.assets.map((asset) => ({
    assetKey: asset.assetKey,
    url: asset.url,
    source: 'server',
  }));
}
